import * as React from 'react';
import { styles } from './styles';
import { fromEvent, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildReactStateHandler, makeDebugger } from '@lib';
import { cx } from 'emotion';
import { IframeSource } from '../Template/services/previewListener/PreviewListener';
import { PreviewInternalAction } from '../Template/services/previewListener/types';
import { EventFragment } from '@graphql/types';
import { disableIframeContent } from 'utils/disableIframeContent';

const stateUpdater = buildReactStateHandler<State>();
const debug = makeDebugger('Preview');

const IFRAME_ID = 'PreviewIframe';

export type Device = 'monitor' | 'tablet' | 'phone';

export interface PreviewProps {
  iframeBackgroundColor: string;
  url: string;
  device: Device;
  deviceShell: boolean;
  onInitialized?: (event: EventFragment) => void;
}

interface State {
  hasInitalizedIframe: boolean;
  device: Device;
  isTransitioningDeviceShell: boolean;
  iframeWidth: number;
  iframeHeight: number;
}

class Preview extends React.Component<PreviewProps, State> {
  static getDerivedStateFromProps(props: PreviewProps, state: State) {
    if (state && props.device !== state.device) {
      return {
        isTransitioningDeviceShell: true
      };
    }
    return null;
  }
  static defaultProps = {
    device: 'monitor',
    deviceShell: true,
    iframeBackgroundColor: '#FFF'
  };

  private wrapperRef: React.RefObject<HTMLDivElement> = React.createRef();
  private containerRef: React.RefObject<HTMLDivElement> = React.createRef();
  private iframeRef: React.RefObject<HTMLIFrameElement> = React.createRef();
  private resizeSub: Subscription;

  state = {
    hasInitalizedIframe: false,
    device: this.props.device,
    isTransitioningDeviceShell: false,
    iframeWidth: 0,
    iframeHeight: 0
  };

  constructor(props: PreviewProps) {
    super(props);
    this.listenForPostmessage();
  }

  componentDidMount() {
    if (this.wrapperRef.current && this.wrapperRef.current.parentElement) {
      const { clientWidth, clientHeight } = this.wrapperRef.current.parentElement;
      this.setState({ iframeWidth: clientWidth, iframeHeight: clientHeight });
    }
    this.resizeSub = fromEvent(window, 'resize')
      .pipe(
        map(() => {
          if (this.wrapperRef.current) {
            if (this.wrapperRef.current.parentElement) {
              const { clientWidth, clientHeight } = this.wrapperRef.current.parentElement;
              return {
                clientWidth,
                clientHeight
              };
            } else {
              return { clientWidth: 0, clientHeight: 0 };
            }
          } else {
            return { clientWidth: 0, clientHeight: 0 };
          }
        })
      )
      .subscribe(sizes => {
        this.setState({ iframeWidth: sizes.clientWidth, iframeHeight: sizes.clientHeight });
      });

    this.observeDeviceTransition();
  }

  componentWillUnmount() {
    this.resizeSub.unsubscribe();
    window.removeEventListener('message', this.receiveMessage);
  }

  observeDeviceTransition = () => {
    if (this.containerRef.current) {
      let timeoutId: number | undefined = undefined;
      this.containerRef.current.addEventListener(
        'transitionend',
        e => {
          clearTimeout(timeoutId);
          timeoutId = window.setTimeout(() => {
            window.requestAnimationFrame(() => {
              this.setState(
                stateUpdater(draft => {
                  draft.device = this.props.device;
                  draft.isTransitioningDeviceShell = false;
                })
              );
            });
          }, 500);
        },
        false
      );
    }
  };

  sendMessage = (message: any) => {
    if (this.iframeRef.current && this.iframeRef.current.contentWindow) {
      this.iframeRef.current.contentWindow.postMessage(JSON.stringify(message), window.config().host!);
    }
  };

  receiveMessage = (event: MessageEvent) => {
    if (event.data.source === IframeSource.joyPreview) {
      try {
        if (event.data.action === PreviewInternalAction.previewInitialized) {
          this.setState(
            stateUpdater(draft => {
              draft.hasInitalizedIframe = true;
            }),
            () => {
              if (this.props.onInitialized) {
                this.props.onInitialized(JSON.parse(event.data.payload));
              }
            }
          );
        }
      } catch (e) {
        debug(e);
      }
    }
  };

  listenForPostmessage = () => {
    window.addEventListener('message', this.receiveMessage, false);
  };

  handleFade = () => {
    this.setState(
      stateUpdater(draft => {
        draft.isTransitioningDeviceShell = !draft.isTransitioningDeviceShell;
      })
    );
  };

  renderDeviceShell = () => {
    if (this.props.deviceShell) {
      switch (this.state.device) {
        case 'monitor':
          return (
            <div className={styles.monitorShell}>
              <div className={styles.monitorCircle} />
              <div className={styles.monitorCircle} />
              <div className={styles.monitorCircle} />
            </div>
          );
        case 'tablet':
          return null;
        case 'phone':
          return (
            <div className={styles.phoneShell}>
              <div className={styles.phoneTop}>
                <div className={styles.phoneCamera} />
                <div className={styles.phoneSpeaker} />
              </div>
              <div className={styles.phoneTrackpad} />
            </div>
          );
        default:
          return null;
      }
    }
    return null;
  };

  iframeLoaded = async () => {
    if (this.iframeRef.current) {
      // disable child of the iframe with the id  #react
      disableIframeContent(IFRAME_ID, 'react');
    }
  };

  render() {
    const { deviceShell } = this.props;
    const { device } = this.state;
    return (
      <div className={styles.previewWrapper}>
        <div
          ref={this.wrapperRef}
          className={styles.iframeWrapper(device, this.state.iframeWidth, this.state.iframeHeight)}
        >
          <div
            ref={this.containerRef}
            className={cx(
              styles.container(
                this.state.isTransitioningDeviceShell,
                device,
                deviceShell,
                this.state.hasInitalizedIframe
              ),
              this.state.isTransitioningDeviceShell && 'is-fading'
            )}
          >
            {this.renderDeviceShell()}
            <iframe
              id={IFRAME_ID}
              ref={this.iframeRef}
              className={styles.iframe}
              frameBorder={0}
              src={this.props.url}
              onLoad={this.iframeLoaded}
            />
          </div>
        </div>
      </div>
    );
  }
}

export { Preview };
